import { useAppSelector } from 'dux/app/hooks';

import { feedbackSubCategories } from 'constants/feedback';

import { getOrderCategory } from 'dux/checkoutOrder/selectors';
import { shouldShowTrialOffer } from 'dux/featureFlags/selectors';
import { getSupplementsLastOrderNumberOfMonthsSinceCreation } from 'dux/feedback/selectors';
import { hasOrderedSupplementsMultipleTimes as hasOrderedSupplementsMultipleTimesSelector } from 'dux/orders/selectors';
import {
  getHasCompletedHaircareConsultation,
  getHasCompletedSkincareConsultation,
  getHasHaircareSubscriptionInAnyState,
  getHasSkincareSubscriptionInAnyState,
} from 'dux/user/selectors';
import { useGetFeedbackStatusesQuery } from 'dux/feedback/apiSlice';

import { getShowSkincareStarterSetPromo } from './selectors';

export const PromoIdentifiers = {
  SKINCARE_MINIS: 'skincare-minis',
  TRIAL_OFFER: 'trial-offer',
  FEEDBACK: 'feedback',
  REFER_A_FRIEND: 'refer-a-friend',
} as const;
export type PromoIdentifiers = (typeof PromoIdentifiers)[keyof typeof PromoIdentifiers];

export type ExcludeByPromoType<PU extends Promotion, Id extends PromoIdentifiers> = PU extends {
  id: Id;
}
  ? never
  : PU;

export const excludePromo = <const T extends PromoIdentifiers, TL extends Promotion[]>(
  piList: TL,
  ...toExclude: T[]
): ExcludeByPromoType<Promotion, T>[] => {
  const excludeSet = new Set<PromoIdentifiers>(toExclude);
  return piList.filter((p): p is ExcludeByPromoType<Promotion, T> => !excludeSet.has(p.id));
};

type PromoContext = ReturnType<typeof usePromoContext>;
const promoConditions = {
  SKINCARE_MINIS: {
    shouldShow: (promoContext: PromoContext) => promoContext.showSkincareStarterSetPromo,
    identifier: PromoIdentifiers.SKINCARE_MINIS,
    computeContext: ({
      hasCompletedSkincareConsultation,
    }: PromoContext): SkincareMinisPromo['context'] => {
      const consultationStatus = hasCompletedSkincareConsultation
        ? 'consultationCompleted'
        : 'consultationNotCompleted';

      return { contentCategory: 'skincare', consultationStatus };
    },
  },
  TRIAL_OFFER: {
    shouldShow: (promoContext: PromoContext) => promoContext.showTrialOffer,
    identifier: PromoIdentifiers.TRIAL_OFFER,
    computeContext: ({
      hasHaircareSubscriptionInAnyState,
      hasSkincareSubscriptionInAnyState,
      hasCompletedHaircareConsultation,
      hasCompletedSkincareConsultation,
      checkoutOrderCategory,
    }: PromoContext): TrialOfferPromo['context'] => {
      switch (checkoutOrderCategory) {
        case 'haircare': {
          const contentCategory = !hasSkincareSubscriptionInAnyState ? 'skincare' : 'disabled';

          if (contentCategory === 'disabled') {
            return { contentCategory } as const;
          }

          const consultationStatus = hasCompletedSkincareConsultation
            ? 'consultationCompleted'
            : 'consultationNotCompleted';

          return { contentCategory, consultationStatus } as const;
        }

        case 'skincare': {
          const contentCategory = !hasHaircareSubscriptionInAnyState ? 'haircare' : 'disabled';

          if (contentCategory === 'disabled') {
            return { contentCategory } as const;
          }

          const consultationStatus = hasCompletedHaircareConsultation
            ? 'consultationCompleted'
            : 'consultationNotCompleted';

          return { contentCategory, consultationStatus } as const;
        }

        case 'accesories': {
          const contentCategory =
            hasHaircareSubscriptionInAnyState && !hasSkincareSubscriptionInAnyState
              ? 'skincare'
              : !hasHaircareSubscriptionInAnyState && hasSkincareSubscriptionInAnyState
              ? 'haircare'
              : 'disabled';

          if (contentCategory === 'disabled') {
            throw new Error('No content category found');
          }

          const consultationStatus =
            (contentCategory === 'haircare' && hasCompletedHaircareConsultation) ||
            (contentCategory === 'skincare' && hasCompletedSkincareConsultation)
              ? 'consultationCompleted'
              : 'consultationNotCompleted';

          return { contentCategory, consultationStatus } as const;
        }

        default: {
          if (!hasHaircareSubscriptionInAnyState && !hasSkincareSubscriptionInAnyState) {
            const consultationStatus =
              hasCompletedHaircareConsultation && hasCompletedSkincareConsultation
                ? 'bothCompleted'
                : hasCompletedHaircareConsultation && !hasCompletedSkincareConsultation
                ? 'haircareCompleted'
                : !hasCompletedHaircareConsultation && hasCompletedSkincareConsultation
                ? 'skincareCompleted'
                : 'neitherCompleted';

            return { contentCategory: 'both', consultationStatus } as const;
          }

          const contentCategory =
            hasHaircareSubscriptionInAnyState && !hasSkincareSubscriptionInAnyState
              ? 'skincare'
              : !hasHaircareSubscriptionInAnyState && hasSkincareSubscriptionInAnyState
              ? 'haircare'
              : 'disabled';

          if (contentCategory === 'disabled') {
            throw new Error('No content category found');
          }

          const consultationStatus =
            (contentCategory === 'haircare' && hasCompletedHaircareConsultation) ||
            (contentCategory === 'skincare' && hasCompletedSkincareConsultation)
              ? 'consultationCompleted'
              : 'consultationNotCompleted';

          return { contentCategory, consultationStatus } as const;
        }
      }
    },
  },
  FEEDBACK: {
    shouldShow: (promoContext: PromoContext) => promoContext.showFeedback,
    identifier: PromoIdentifiers.FEEDBACK,
    computeContext: (_promoContext: PromoContext): FeedbackPromo['context'] => ({
      contentCategory: 'default',
    }),
  },
  REFER_A_FRIEND: {
    shouldShow: (_promoContext: ReturnType<typeof usePromoContext>) => true,
    identifier: PromoIdentifiers.REFER_A_FRIEND,
    computeContext: (_promoContext: PromoContext): ReferAFriendPromo['context'] => ({
      contentCategory: 'default',
    }),
  },
} as const;

const usePromoContext = () => {
  const showSkincareStarterSetPromo: boolean = useAppSelector(getShowSkincareStarterSetPromo);
  const trialOfferVariantOrBoolean = useAppSelector(shouldShowTrialOffer);
  const showTrialOffer = Boolean(trialOfferVariantOrBoolean);

  const hasHaircareSubscriptionInAnyState: boolean = useAppSelector(
    getHasHaircareSubscriptionInAnyState,
  );
  const hasSkincareSubscriptionInAnyState: boolean = useAppSelector(
    getHasSkincareSubscriptionInAnyState,
  );

  const hasCompletedHaircareConsultation = useAppSelector(getHasCompletedHaircareConsultation);
  const hasCompletedSkincareConsultation = useAppSelector(getHasCompletedSkincareConsultation);

  const { data: feedbackStatusesData = [] } = useGetFeedbackStatusesQuery();

  const hasOnlySupplementsFeedback =
    feedbackStatusesData?.length > 0
      ? feedbackStatusesData
          .filter((item) => item.status === 'created' || item.status === 'not_started')
          .every((item) => item.sub_category === feedbackSubCategories.SUPPLEMENTS)
      : false;

  const supplementsNumberOfMonthsSinceCreation = useAppSelector(
    getSupplementsLastOrderNumberOfMonthsSinceCreation,
  );
  const hasOrderedSupplementsMultipleTimes = useAppSelector(
    hasOrderedSupplementsMultipleTimesSelector,
  );

  const checkoutOrderCategory: 'haircare' | 'skincare' | 'accesories' | undefined =
    useAppSelector(getOrderCategory);

  const isReviewSupplementsAvailable =
    supplementsNumberOfMonthsSinceCreation >= 1 || hasOrderedSupplementsMultipleTimes;

  const showFeedback =
    feedbackStatusesData.length > 0 &&
    (!hasOnlySupplementsFeedback || (hasOnlySupplementsFeedback && isReviewSupplementsAvailable));

  return {
    showSkincareStarterSetPromo,
    showTrialOffer,
    showFeedback,
    hasHaircareSubscriptionInAnyState,
    hasSkincareSubscriptionInAnyState,
    hasCompletedHaircareConsultation,
    hasCompletedSkincareConsultation,
    checkoutOrderCategory,
  };
};

const promotionsPriority = [
  promoConditions.SKINCARE_MINIS,
  promoConditions.TRIAL_OFFER,
  promoConditions.FEEDBACK,
  promoConditions.REFER_A_FRIEND,
] as const;

type NoConsultationStatus = {
  contentCategory: 'default';
};

type OneCategoryConsultationStatus = {
  contentCategory: 'haircare' | 'skincare';
  consultationStatus: 'consultationNotCompleted' | 'consultationCompleted';
};

type BothCategoriesConsultationStatus = {
  contentCategory: 'both';
  consultationStatus:
    | 'haircareCompleted'
    | 'skincareCompleted'
    | 'bothCompleted'
    | 'neitherCompleted';
};

type CustomerConsultationStatus = OneCategoryConsultationStatus | BothCategoriesConsultationStatus;

type DisabledPromo = {
  contentCategory: 'disabled';
};

type SkincareMinisPromo = {
  id: Extract<PromoIdentifiers, 'skincare-minis'>;
  context: OneCategoryConsultationStatus;
};

type TrialOfferPromo = {
  id: Extract<PromoIdentifiers, 'trial-offer'>;
  context: CustomerConsultationStatus | DisabledPromo;
};

type FeedbackPromo = { id: Extract<PromoIdentifiers, 'feedback'>; context: NoConsultationStatus };

type ReferAFriendPromo = {
  id: Extract<PromoIdentifiers, 'refer-a-friend'>;
  context: NoConsultationStatus;
};

export type Promotion = SkincareMinisPromo | TrialOfferPromo | FeedbackPromo | ReferAFriendPromo;

/**
 * Returns a tuple of the possible identifers values and a list of promotions identifiers that should be shown
 */
export const usePromoIdentifiers = (): {
  PromoIdentifiers: typeof PromoIdentifiers;
  availablePromoList: Promotion[];
} => {
  const promoContext = usePromoContext();

  const availablePromoList = promotionsPriority
    .filter((promo) => promo.shouldShow(promoContext))
    .map(
      (promo) =>
        ({ id: promo.identifier, context: promo.computeContext(promoContext) }) as Promotion,
    );

  return { PromoIdentifiers, availablePromoList };
};
